import { Alert } from '@cluey/cluey-components';
import { get, sortBy } from 'lodash';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { api } from '../../../../api';
import { Enrolment } from '../../../../api/types/enrolment';
import ErrorBlock from '../../../../common/ErrorBlock';
import { ContextualAlert } from '../../../../components/ContextualAlert/ContextualAlert';
import NextYearEnrolmentsAlerts from '../../../../components/EnrolmentManagement/NextYearEnrolmentsAlerts';
import { Loader } from '../../../../components/Loader/Loader';
import RecordsPagination from '../../../../components/hub/RecordsPagination';
import { HubSessionsListItem } from '../../../../components/hub/sessions/HubSessionsListItem';
import SchoolTermListItem from '../../../../components/hub/sessions/SchoolTermListItem';
import { isLoadingUpcomingSessionsData } from '../../../../selectors/hubSelector';
import { getSummerModeCompleted } from '../../../../selectors/summerModeSelector';
import { NO_OF_SESSIONS_PER_PAGE, SPECIAL_DATE_TYPE } from '../../../../util/constants';
import { convertSchoolTerms } from '../../../../util/helper/schoolTerm';
import { filterSchoolTerms, getSessionsInViewByIndex } from '../../../../util/helpers';
import { PATH_HUB_CR_OTHER_QUERY, PATH_HUB_ENROLMENT_DETAIL } from '../../../../util/pagePath';
import { SELECTED_SUMMER_MODE } from '../../../../util/summerCopy';
import { useGetBulkSessionPolicy } from '../../../../v2/common/hooks/useGetBulkSessionPolicy';
import useSessionContext from '../useSessionContext';

const UpcomingSessions = () => {
  const {
    filteredSessions: { upcoming: upcomingSessions = [] },
    fetchSessionIsLoading: upcomingSessionsLoading,
    fetchSessionIsError: upcomingSessionsError,
  } = useSessionContext();
  const { data: terms, isLoading: termsLoading } = api.schoolTerm.all.useQuery();
  const { data: accountDetails, isLoading: accountDetailsLoading } = api.account.details.useQuery();
  const { data: sessionPolicies, isLoading: isLoadingSessionPolicies } = useGetBulkSessionPolicy({
    sessionIds: upcomingSessions.map((session) => session.studentSessionId),
    enabled: !!upcomingSessions,
  });

  const [{ sessionsInView, totalSessions, startIndex }, setSessionDetails] = useState({
    allSessions: [],
    totalSessions: 0,
    sessionsInView: [],
    startIndex: 0,
  });
  const activeSpinner = useSelector(isLoadingUpcomingSessionsData);
  const timezone = accountDetails?.timezone ?? 'Australia/Sydney';
  const schoolTerms = convertSchoolTerms({ terms });
  const summerModeAlerts: Array<Enrolment> = useSelector(getSummerModeCompleted);
  const isLoading =
    activeSpinner || upcomingSessionsLoading || termsLoading || accountDetailsLoading || isLoadingSessionPolicies;

  const sortedSessions = useMemo(() => {
    if (!upcomingSessions?.length) return [];
    return sortBy(upcomingSessions, ['startTime', 'student', 'subject']);
  }, [upcomingSessions]);

  const mixedSessionsAndTerms = useMemo(() => {
    if (!sortedSessions.length) return [];
    const lastSession = sortedSessions[sortedSessions.length - 1];
    const filteredSchoolTerms = filterSchoolTerms(schoolTerms, lastSession.startTime);
    return [...sortedSessions, ...filteredSchoolTerms, ...summerModeAlerts];
  }, [sortedSessions, schoolTerms, summerModeAlerts]);

  const sortedMixedSessionsAndTerms = useMemo(() => {
    return sortBy(mixedSessionsAndTerms, ['startTime', 'student', 'subject']);
  }, [mixedSessionsAndTerms]);

  useEffect(() => {
    if (sortedMixedSessionsAndTerms.length > 0) {
      const sessionsInView = getSessionsInViewByIndex(sortedMixedSessionsAndTerms, startIndex);
      setSessionDetails((current) => {
        if (
          current.totalSessions === sortedMixedSessionsAndTerms.length &&
          JSON.stringify(current.sessionsInView) === JSON.stringify(sessionsInView)
        ) {
          return current;
        }
        return {
          allSessions: sortedMixedSessionsAndTerms,
          totalSessions: sortedMixedSessionsAndTerms.length,
          startIndex,
          sessionsInView,
        };
      });
    } else {
      setSessionDetails((current) => {
        if (current.totalSessions === 0 && current.sessionsInView.length === 0) {
          return current;
        }
        return {
          allSessions: [],
          totalSessions: 0,
          sessionsInView: [],
          startIndex: 0,
        };
      });
    }
  }, [sortedMixedSessionsAndTerms, startIndex, sessionPolicies]);

  const handlePaginationClick = (newStartIndex) => {
    setSessionDetails((current) => {
      return {
        ...current,
        sessionsInView: getSessionsInViewByIndex(current.allSessions, newStartIndex),
        startIndex: newStartIndex,
      };
    });
  };

  return (
    <Fragment>
      {isLoading ? (
        <Loader />
      ) : (
        <Fragment>
          {upcomingSessionsError && <Alert type="error" body="There was an error retrieving your upcoming sessions" />}
          {!upcomingSessionsError &&
            (sortedMixedSessionsAndTerms?.length === 0 ? (
              <ErrorBlock errorMsg="You have no upcoming sessions." />
            ) : (
              <Fragment>
                <div className="flex flex-col gap-4">
                  {sessionsInView.map((s, index) => {
                    const isLastItem = sessionsInView.length === index + 1;
                    const type = get(s, 'type', '').toLowerCase();
                    if (type === SPECIAL_DATE_TYPE.SCHOOL_TERM) {
                      return (
                        <SchoolTermListItem
                          key={s.startTime}
                          termItem={s}
                          timezone={timezone}
                          isLastItem={isLastItem}
                        />
                      );
                    }
                    if (type === SPECIAL_DATE_TYPE.SUMMER_ALERT) {
                      return (
                        <ContextualAlert
                          key={s.id}
                          body={s.body}
                          subtitle={s.subtitle}
                          title={s.title}
                          cta={
                            s.action === SELECTED_SUMMER_MODE.PAUSE
                              ? { to: `${PATH_HUB_ENROLMENT_DETAIL}?token=${s.id}`, text: 'Manage enrolment' }
                              : { to: PATH_HUB_CR_OTHER_QUERY, text: 'Contact us to manage' }
                          }
                          ctaWrap={s.action !== SELECTED_SUMMER_MODE.PAUSE}
                        />
                      );
                    }
                    return (
                      <HubSessionsListItem
                        key={s.uniqueEnrolmentId + s.startTime}
                        session={s}
                        timezone={timezone}
                        policy={sessionPolicies?.[s.studentSessionId]}
                      />
                    );
                  })}
                </div>
                <NextYearEnrolmentsAlerts />
                <RecordsPagination
                  recordsTotal={totalSessions}
                  noOfRecordsPerPage={NO_OF_SESSIONS_PER_PAGE}
                  handlePaginationClick={handlePaginationClick}
                  pageIndex={startIndex}
                />
              </Fragment>
            ))}
        </Fragment>
      )}
    </Fragment>
  );
};

export default UpcomingSessions;
